import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppState } from '../redux/reducers';
import { ErrorType } from '../models/HttpError';
import { Languaje } from '../models/Entities/Settings/Languajes';
import { GetUserSetting, PutUserSetting } from '../services/UserSettings.Services';
import { GetLanguajes } from '../services/Languajes.Services';
import * as UserSettingsStore from '../redux/middleware/UserMiddleware';
import { setBlueLight, setBlueLightLevel, setThemeMode } from '../redux/actions/Theme.Actions';
import { DEFAULT_THEME_MODE, ThemeMode, isThemeMode } from './guideTokens';
import { DEFAULT_BLUE_LIGHT_LEVEL, clampBlueLightLevel } from './blueLight';
import { writeBlueLightLevel, writeBlueLightPreference, writeThemePreference } from './ThemePreference';

/** Clave en Persons.API (`/persons/user-settings/{key}`). */
export const UI_PREFERENCES_KEY = 'ui.preferences';
/** Espera entre el último cambio y el PUT (el graduador de luz azul dispara muchos seguidos). */
export const UI_PREFERENCES_SAVE_DEBOUNCE_MS = 1000;

export const UI_LANGUAGE_CODES = ['es', 'en', 'pr'];

/** Lo que viaja con la cuenta: tema, filtro de luz azul y su intensidad, e idioma de la interfaz. */
export interface UiPreferences {
  version: 1;
  theme: ThemeMode;
  blueLight: boolean;
  blueLightLevel: number;
  language?: string;
}

export const defaultUiPreferences = (): UiPreferences => ({
  version: 1,
  theme: DEFAULT_THEME_MODE,
  blueLight: false,
  blueLightLevel: DEFAULT_BLUE_LIGHT_LEVEL,
  language: 'es',
});

export const serializeUiPreferences = (prefs: UiPreferences): string =>
  JSON.stringify({ version: 1, theme: prefs.theme, blueLight: prefs.blueLight, blueLightLevel: prefs.blueLightLevel, language: prefs.language });

/** Valida lo que vino del servidor; campos inválidos caen al valor por defecto. `undefined` si no es un objeto. */
export const parseUiPreferences = (value: string | null | undefined): UiPreferences | undefined => {
  if (!value) return undefined;
  try {
    const raw = JSON.parse(value) as Partial<UiPreferences> | null;
    if (!raw || typeof raw !== 'object') return undefined;
    const defaults = defaultUiPreferences();
    return {
      version: 1,
      theme: isThemeMode(raw.theme) ? raw.theme : defaults.theme,
      blueLight: typeof raw.blueLight === 'boolean' ? raw.blueLight : defaults.blueLight,
      blueLightLevel: clampBlueLightLevel(raw.blueLightLevel ?? defaults.blueLightLevel),
      language: typeof raw.language === 'string' && UI_LANGUAGE_CODES.includes(raw.language) ? raw.language : defaults.language,
    };
  } catch {
    return undefined;
  }
};

export type UiPreferencesSyncStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'local';

const isNotFound = (error: unknown): boolean => (error as ErrorType | undefined)?.ErrorCode === 404;

const LANGUAGE_DESCRIPTIONS: Record<string, string> = { es: 'Español', en: 'English', pr: 'Português' };

const languageOf = async (code: string): Promise<Languaje> => {
  try {
    const known = (await GetLanguajes()).find((item) => item.code === code);
    if (known) return known;
  } catch {
    // sin catálogo: se arma con la descripción conocida
  }
  return { code, description: LANGUAGE_DESCRIPTIONS[code] ?? code };
};

/**
 * Sincroniza con la cuenta (Persons.API) el tema, el filtro de luz azul y el idioma, que ya se guardan
 * por usuario en localStorage: al entrar se aplica lo de la cuenta (gana sobre lo local), y cada cambio
 * posterior se sube con una pequeña espera. Sin sesión o sin servidor, todo sigue funcionando en local.
 * Se usa una sola vez, en `AppThemeProvider`.
 */
export const useUiPreferencesSync = (): UiPreferencesSyncStatus => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const userId = useSelector((state: AppState) => {
    const sub = state.oidc?.user?.profile?.sub;
    return sub ? String(sub) : undefined;
  });
  const token = useSelector((state: AppState) => state.oidc?.user?.access_token as string | undefined);
  const theme = useSelector((state: AppState) => state.Theme?.mode ?? DEFAULT_THEME_MODE);
  const blueLight = useSelector((state: AppState) => state.Theme?.blueLight ?? false);
  const blueLightLevel = useSelector((state: AppState) => state.Theme?.blueLightLevel ?? DEFAULT_BLUE_LIGHT_LEVEL);
  const storeLanguage = useSelector((state: AppState) => state.UserSettings?.CurrentUser?.UserLanguaje?.code as string | undefined);
  const language = storeLanguage || i18n?.language || 'es';

  const [status, setStatus] = React.useState<UiPreferencesSyncStatus>('idle');
  /** Serialización de lo último que el servidor tiene: evita PUTs sin cambios. */
  const syncedRef = React.useRef<string | undefined>(undefined);
  /** Hasta que no se leyó la cuenta, los cambios locales no se suben (serían los valores de arranque). */
  const readyRef = React.useRef(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingRef = React.useRef<UiPreferences | undefined>(undefined);
  const tokenRef = React.useRef(token);
  tokenRef.current = token;
  // La instancia de i18n se lee por ref: si el hook de traducción devolviera un objeto nuevo por render,
  // el efecto de lectura se relanzaría en cada render.
  const i18nRef = React.useRef(i18n);
  i18nRef.current = i18n;

  const current = React.useMemo<UiPreferences>(
    () => ({ version: 1, theme, blueLight, blueLightLevel, language }),
    [theme, blueLight, blueLightLevel, language]
  );

  const flush = React.useCallback(async () => {
    timerRef.current = undefined;
    const next = pendingRef.current;
    pendingRef.current = undefined;
    const currentToken = tokenRef.current;
    if (!next || !currentToken) return;
    const serialized = serializeUiPreferences(next);
    if (serialized === syncedRef.current) return;
    setStatus('saving');
    try {
      await PutUserSetting(currentToken, UI_PREFERENCES_KEY, serialized);
      syncedRef.current = serialized;
      setStatus('saved');
    } catch {
      setStatus('local');
    }
  }, []);

  const scheduleSave = React.useCallback(
    (next: UiPreferences) => {
      if (!tokenRef.current) return;
      pendingRef.current = next;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void flush();
      }, UI_PREFERENCES_SAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  /** Aplica lo de la cuenta en el store y en las copias locales, sólo donde difiere. */
  const applyRemote = React.useCallback(
    async (remote: UiPreferences, local: UiPreferences) => {
      if (remote.theme !== local.theme) {
        writeThemePreference(userId, remote.theme);
        dispatch(setThemeMode(remote.theme, userId));
      }
      if (remote.blueLight !== local.blueLight) {
        writeBlueLightPreference(userId, remote.blueLight);
        dispatch(setBlueLight(remote.blueLight, userId));
      }
      if (remote.blueLightLevel !== local.blueLightLevel) {
        writeBlueLightLevel(userId, remote.blueLightLevel);
        dispatch(setBlueLightLevel(remote.blueLightLevel, userId));
      }
      if (remote.language && remote.language !== local.language) {
        const languaje = await languageOf(remote.language);
        await i18nRef.current?.changeLanguage?.(remote.language);
        dispatch(UserSettingsStore.ActionCreators.SetUserLanguaje(languaje) as any);
      }
    },
    [dispatch, userId]
  );

  const currentRef = React.useRef(current);
  currentRef.current = current;

  // Al entrar (o cambiar de usuario) se lee la cuenta.
  React.useEffect(() => {
    let cancelled = false;
    readyRef.current = false;
    syncedRef.current = undefined;
    if (!userId || !token) {
      setStatus('idle');
      return undefined;
    }
    setStatus('loading');
    GetUserSetting(token, UI_PREFERENCES_KEY)
      .then(async (setting) => {
        if (cancelled) return;
        const remote = parseUiPreferences(setting?.value);
        if (remote) {
          syncedRef.current = serializeUiPreferences(remote);
          await applyRemote(remote, currentRef.current);
          if (cancelled) return;
          readyRef.current = true;
          setStatus('saved');
          return;
        }
        readyRef.current = true;
        scheduleSave(currentRef.current);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        readyRef.current = true;
        if (isNotFound(error)) {
          // Primera vez en la cuenta: si acá ya había algo distinto de lo de fábrica, se sube.
          if (serializeUiPreferences(currentRef.current) !== serializeUiPreferences(defaultUiPreferences())) scheduleSave(currentRef.current);
          else setStatus('idle');
          return;
        }
        setStatus('local');
      });
    return () => {
      cancelled = true;
    };
  }, [userId, token, applyRemote, scheduleSave]);

  // Cada cambio local posterior a la lectura se sube.
  React.useEffect(() => {
    if (!readyRef.current) return;
    if (serializeUiPreferences(current) === syncedRef.current) return;
    scheduleSave(current);
  }, [current, scheduleSave]);

  return status;
};
